import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { Plus, Send, X, Copy, Link2, Mail, Ticket, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const STATUS_COLOR = {
    sent: "text-[#00e676]",
    pending: "text-[#ffea00]",
    active: "text-[#00e676]",
    failed: "text-[#ff3333]",
    revoked: "text-[#a3a3a3]",
    accepted: "text-[#00e676]",
    expired: "text-[#a3a3a3]",
    unconfigured: "text-[#ffea00]",
};

const EXPIRY_OPTIONS = [
    { value: 24, label: "24 hours" },
    { value: 72, label: "3 days" },
    { value: 168, label: "7 days" },
];

const ROLE_LABEL = { team: "Team Member", staff: "Event Staff" };
const CHANNELS = [
    { value: "", label: "—" },
    { value: "whatsapp", label: "WhatsApp" },
    { value: "sms", label: "SMS" },
    { value: "instagram_dm", label: "Instagram DM" },
    { value: "email", label: "Email" },
    { value: "in_person", label: "In person" },
    { value: "other", label: "Other" },
];
const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map((c) => [c.value, c.label]));

export default function Team() {
    const { activeArtistId } = useRole();
    const [team, setTeam] = useState([]);
    const [invites, setInvites] = useState([]);
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [inviteOpen, setInviteOpen] = useState(false);
    const [linkOpen, setLinkOpen] = useState(false);
    const [accessRequests, setAccessRequests] = useState([]);
    const [decidingId, setDecidingId] = useState(null);

    const load = () => {
        if (!activeArtistId) return;
        endpoints.team(activeArtistId).then((d) => setTeam(d.team));
        endpoints.authListInvites(activeArtistId).then((d) => setInvites(d.invitations));
        endpoints.authShareLinkSummary(activeArtistId).then(setSummary).catch(() => setSummary(null));
        endpoints.workspaceEvents(activeArtistId).then((d) => setEvents(d.all || [])).catch(() => setEvents([]));
        endpoints.workspaceAccessRequests(activeArtistId).then((d) => setAccessRequests(d.requests || [])).catch(() => setAccessRequests([]));
    };
    useEffect(load, [activeArtistId]);

    const approveRequest = async (req, role = "manager") => {
        setDecidingId(req.id);
        try {
            await endpoints.approveAccessRequest(activeArtistId, req.id, { granted_role: role });
            const label = role === "manager" ? "Manager/Admin" : role === "staff" ? "Event Staff" : "Team Member";
            toast.success(`${req.user_name || req.user_email} added as ${label}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setDecidingId(null); }
    };
    const rejectRequest = async (req) => {
        if (!window.confirm(`Reject ${req.user_name || req.user_email}'s access request?`)) return;
        setDecidingId(req.id);
        try {
            await endpoints.rejectAccessRequest(activeArtistId, req.id);
            toast.success("Request declined");
            load();
        } catch (e) { toast.error(formatApiError(e)); } finally { setDecidingId(null); }
    };

    const resend = async (inv) => {
        try {
            const r = await endpoints.authResendInvite(activeArtistId, inv.id);
            if (r.email_sent) toast.success("Invitation resent");
            else toast.error(r.delivery_message || "Resend failed");
            if (r.dev_invite_link) toast(`Dev link: ${r.dev_invite_link}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const revoke = async (inv) => {
        const label = inv.kind === "share_link" ? "this share link" : `the invitation for ${inv.email}`;
        if (!window.confirm(`Revoke ${label}?`)) return;
        try { await endpoints.authRevokeInvite(activeArtistId, inv.id); toast.success("Invitation revoked"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const copyLink = (inv) => {
        const url = `${window.location.origin}/accept-invite?token=${inv.token}`;
        navigator.clipboard.writeText(url);
        toast.success("Invite link copied");
    };

    const emailInvites = invites.filter((i) => (i.kind || "email") === "email");
    const linkInvites = invites.filter((i) => i.kind === "share_link");
    const pending = emailInvites.filter((i) => !i.accepted_by && !i.revoked);
    const past = emailInvites.filter((i) => i.accepted_by || i.revoked);
    const activeLinks = linkInvites.filter((i) => i.status === "active");
    const inactiveLinks = linkInvites.filter((i) => i.status !== "active");

    return (
        <div className="pm-fade" data-testid="team-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Team</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Workspace access</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Dialog open={linkOpen} onOpenChange={setLinkOpen}>
                        <DialogTrigger asChild>
                            <button className="pm-btn" data-testid="team-share-link-btn"><Link2 className="w-4 h-4 mr-2" /> Create invite link</button>
                        </DialogTrigger>
                        <ShareLinkDialog artistId={activeArtistId} events={events} onDone={() => { setLinkOpen(false); load(); }} />
                    </Dialog>
                    <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
                        <DialogTrigger asChild>
                            <button className="pm-btn pm-btn-primary" data-testid="team-invite-btn"><Plus className="w-4 h-4 mr-2" /> Invite by email</button>
                        </DialogTrigger>
                        <InviteDialog artistId={activeArtistId} onDone={() => { setInviteOpen(false); load(); }} />
                    </Dialog>
                </div>
            </header>

            <div className="px-4 md:px-8 py-6 space-y-8">
                {accessRequests.filter((r) => r.status === "pending" || r.status === "orphaned").length > 0 && (
                    <section data-testid="team-access-requests">
                        <div className="text-[10px] font-mono uppercase text-[#ff4400] mb-2">Pending access requests</div>
                        <div className="border border-[#ff4400]/40">
                            {accessRequests.filter((r) => r.status === "pending" || r.status === "orphaned").map((r) => (
                                <div key={r.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center p-4 border-b border-[#2a2a2a] last:border-b-0" data-testid={`team-access-req-${r.id}`}>
                                    <div className="md:col-span-5">
                                        <div className="font-display font-bold uppercase text-sm">{r.user_name || r.user_email}</div>
                                        <div className="text-[10px] font-mono uppercase text-[#666]">{r.user_email}</div>
                                    </div>
                                    <div className="md:col-span-3 text-[10px] font-mono uppercase text-[#a3a3a3]">
                                        Relationship: <span className="text-[#ff9500]">{r.relationship}</span>
                                    </div>
                                    <div className="md:col-span-4 flex justify-start md:justify-end gap-1 flex-wrap">
                                        <button
                                            onClick={() => approveRequest(r, "manager")}
                                            disabled={decidingId === r.id}
                                            className="pm-btn pm-btn-primary text-[10px]"
                                            data-testid={`team-access-approve-manager-${r.id}`}
                                            title="Full artist-management access (not ownership)"
                                        >
                                            Approve as Manager/Admin
                                        </button>
                                        <button
                                            onClick={() => approveRequest(r, "team")}
                                            disabled={decidingId === r.id}
                                            className="pm-btn text-[10px]"
                                            data-testid={`team-access-approve-${r.id}`}
                                        >
                                            Approve as team
                                        </button>
                                        <button
                                            onClick={() => approveRequest(r, "staff")}
                                            disabled={decidingId === r.id}
                                            className="pm-btn text-[10px]"
                                            data-testid={`team-access-approve-staff-${r.id}`}
                                        >
                                            Approve as staff
                                        </button>
                                        <button
                                            onClick={() => rejectRequest(r)}
                                            disabled={decidingId === r.id}
                                            className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]"
                                            data-testid={`team-access-reject-${r.id}`}
                                        >
                                            Reject
                                        </button>
                                    </div>
                                    {r.message && (
                                        <div className="md:col-span-12 text-xs text-[#a3a3a3] italic border-t border-[#2a2a2a] pt-2">"{r.message}"</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <section>
                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2">Members</div>
                    <div className="border border-[#2a2a2a]">
                        {team.length === 0 ? <div className="p-4 text-xs text-[#a3a3a3]">No members yet.</div>
                        : team.map((m) => (
                            <div key={m.id} className="flex items-center gap-4 p-4 border-b border-[#2a2a2a] last:border-b-0" data-testid={`team-member-${m.id}`}>
                                <img src={m.avatar} alt="" className="w-10 h-10 object-cover grayscale" />
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold uppercase">{m.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666]">{m.email}</div>
                                </div>
                                <div className="text-[10px] font-mono uppercase px-3 py-1 border border-[#2a2a2a] text-[#ff4400]">{m.role}</div>
                            </div>
                        ))}
                    </div>
                </section>

                <section>
                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2 flex items-center gap-2"><Link2 className="w-3 h-3" /> Active invite links</div>
                    {summary && (
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[#2a2a2a] border border-[#2a2a2a] mb-3" data-testid="team-share-summary">
                            <Metric label="Active" value={summary.active_links} testId="team-share-active" />
                            <Metric label="Unique clicks" value={summary.total_unique_clicks} testId="team-share-clicks" />
                            <Metric label="Accepted" value={summary.accepted_memberships} testId="team-share-accepted" />
                            <Metric label="Conversion" value={`${summary.conversion_rate}%`} testId="team-share-conv" />
                            <Metric label="Best channel" value={summary.best_channel && summary.best_channel.channel !== "unlabeled" ? (CHANNEL_LABEL[summary.best_channel.channel] || summary.best_channel.channel) : "—"} testId="team-share-best" />
                        </div>
                    )}
                    <div className="border border-[#2a2a2a]">
                        {activeLinks.length === 0 ? <div className="p-4 text-xs text-[#a3a3a3]" data-testid="team-share-links-empty">No active share links.</div>
                        : activeLinks.map((i) => (
                            <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm" data-testid={`team-share-row-${i.id}`}>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase truncate" data-testid={`team-share-role-${i.id}`}>
                                    {ROLE_LABEL[i.role] || i.role}
                                </div>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3] truncate">
                                    {(i.event_names && i.event_names[0]) || (i.role === "staff" ? "—" : "all events")}
                                </div>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]" data-testid={`team-share-analytics-${i.id}`}>
                                    <TrendingUp className="w-3 h-3 inline mr-1" /> {i.total_clicks || 0} clicks · {i.unique_clicks || 0} unique · {i.conversion_rate || 0}%
                                </div>
                                <div className="col-span-6 md:col-span-1 text-[10px] font-mono uppercase text-[#ff9500]" data-testid={`team-share-channel-${i.id}`}>
                                    {i.channel ? (CHANNEL_LABEL[i.channel] || i.channel) : ""}
                                </div>
                                <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#666]">
                                    exp {i.expires_at?.slice(0, 10)}
                                </div>
                                <div className="col-span-6 md:col-span-3 flex justify-end gap-1 flex-wrap">
                                    <button onClick={() => copyLink(i)} className="pm-btn text-[10px]" data-testid={`team-share-copy-${i.id}`}><Copy className="w-3 h-3 mr-1" /> Copy link</button>
                                    <button onClick={() => revoke(i)} className="pm-btn text-[10px]" data-testid={`team-share-revoke-${i.id}`}><X className="w-3 h-3 mr-1" /> Revoke</button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {inactiveLinks.length > 0 && (
                        <details className="mt-2 border border-[#2a2a2a]" data-testid="team-share-history-toggle">
                            <summary className="cursor-pointer px-3 py-2 text-[10px] font-mono uppercase text-[#666]">Past links ({inactiveLinks.length})</summary>
                            {inactiveLinks.map((i) => (
                                <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-t border-[#2a2a2a] text-xs opacity-70" data-testid={`team-share-history-${i.id}`}>
                                    <div className="col-span-6 md:col-span-3 font-mono text-[10px] uppercase">{ROLE_LABEL[i.role] || i.role}</div>
                                    <div className="col-span-6 md:col-span-3 font-mono text-[10px] uppercase text-[#a3a3a3]">{(i.event_names && i.event_names[0]) || "—"}</div>
                                    <div className={`col-span-6 md:col-span-2 text-[10px] font-mono uppercase ${STATUS_COLOR[i.status]}`}>{i.status}</div>
                                    <div className="col-span-6 md:col-span-4 text-[10px] font-mono uppercase text-[#666]">
                                        {i.accepted_by_name ? `accepted by ${i.accepted_by_name}` : ""}
                                    </div>
                                </div>
                            ))}
                        </details>
                    )}
                </section>

                <section>
                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2 flex items-center gap-2"><Mail className="w-3 h-3" /> Pending email invitations</div>
                    <div className="border border-[#2a2a2a]">
                        {pending.length === 0 ? <div className="p-4 text-xs text-[#a3a3a3]" data-testid="team-invites-empty">No pending invitations.</div>
                        : pending.map((i) => (
                            <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm" data-testid={`team-invite-row-${i.id}`}>
                                <div className="col-span-12 md:col-span-4 truncate font-mono text-xs">{i.email}</div>
                                <div className="col-span-3 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">{i.role}</div>
                                <div className={`col-span-4 md:col-span-2 text-[10px] font-mono uppercase ${STATUS_COLOR[i.delivery_status] || "text-[#a3a3a3]"}`} data-testid={`team-invite-status-${i.id}`}>
                                    {i.delivery_status || "pending"}
                                </div>
                                <div className="col-span-5 md:col-span-2 text-[10px] font-mono uppercase text-[#666]">last sent {i.last_sent_at ? i.last_sent_at.slice(0, 10) : "—"}</div>
                                <div className="col-span-12 md:col-span-2 flex justify-end gap-1 flex-wrap">
                                    <button onClick={() => copyLink(i)} className="pm-btn text-[10px]" data-testid={`team-invite-copy-${i.id}`}><Copy className="w-3 h-3 mr-1" /> Link</button>
                                    <button onClick={() => resend(i)} className="pm-btn text-[10px]" data-testid={`team-invite-resend-${i.id}`}><Send className="w-3 h-3 mr-1" /> Resend</button>
                                    <button onClick={() => revoke(i)} className="pm-btn text-[10px]" data-testid={`team-invite-revoke-${i.id}`}><X className="w-3 h-3 mr-1" /> Revoke</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {past.length > 0 && (
                    <section>
                        <div className="text-[10px] font-mono uppercase text-[#666] mb-2">Email history</div>
                        <div className="border border-[#2a2a2a]">
                            {past.map((i) => (
                                <div key={i.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-xs opacity-70" data-testid={`team-invite-history-${i.id}`}>
                                    <div className="col-span-12 md:col-span-5 truncate font-mono">{i.email}</div>
                                    <div className="col-span-6 md:col-span-2 text-[10px] font-mono uppercase text-[#a3a3a3]">{i.role}</div>
                                    <div className="col-span-6 md:col-span-5 text-[10px] font-mono uppercase text-[#a3a3a3]">{i.accepted_by ? `accepted ${i.accepted_at?.slice(0, 10)}` : `revoked ${i.revoked_at?.slice(0, 10) || ""}`}</div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

function Metric({ label, value, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-3" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className="mt-1 font-display font-bold text-lg truncate">{value}</div>
        </div>
    );
}

function InviteDialog({ artistId, onDone }) {
    const [email, setEmail] = useState("");
    const [role, setRole] = useState("team");
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!email) { toast.error("Email required"); return; }
        setBusy(true);
        try {
            const r = await endpoints.authCreateInvite(artistId, { email, role });
            if (r.email_sent) toast.success("Invitation sent");
            else toast(r.delivery_message || "Invitation saved; email delivery unavailable");
            if (r.dev_invite_link) toast(`Dev link: ${r.dev_invite_link}`);
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Invite by email</DialogTitle></DialogHeader>
            <div className="space-y-4">
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Email</span>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none" data-testid="team-invite-email" />
                </label>
                <label className="block">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Role</span>
                    <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="team-invite-role">
                        <option value="team" className="bg-[#141414]">Team Member</option>
                        <option value="staff" className="bg-[#141414]">Event Staff</option>
                    </select>
                </label>
            </div>
            <DialogFooter>
                <button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="team-invite-submit">Send invite</button>
            </DialogFooter>
        </DialogContent>
    );
}

function ShareLinkDialog({ artistId, events, onDone }) {
    const [role, setRole] = useState("team");
    const [eventId, setEventId] = useState("");
    const [expiresHours, setExpiresHours] = useState(72);
    const [channel, setChannel] = useState("");
    const [busy, setBusy] = useState(false);
    const [created, setCreated] = useState(null);

    const submit = async () => {
        if (role === "staff" && !eventId) { toast.error("Select an event for Event Staff"); return; }
        setBusy(true);
        try {
            const payload = { role, expires_in_hours: expiresHours };
            if (role === "staff") payload.event_id = eventId;
            if (channel) payload.channel = channel;
            const r = await endpoints.authCreateShareLink(artistId, payload);
            const url = `${window.location.origin}/accept-invite?token=${r.token}`;
            setCreated({ url, ...r });
            toast.success("Share link created");
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const copyCreated = () => {
        if (!created) return;
        navigator.clipboard.writeText(created.url);
        toast.success("Link copied");
    };
    const close = () => {
        setCreated(null);
        setRole("team");
        setEventId("");
        setExpiresHours(72);
        onDone();
    };

    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-md">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">Create shareable link</DialogTitle></DialogHeader>
            {!created ? (
                <>
                    <div className="space-y-4">
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Role</span>
                            <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="team-share-role">
                                <option value="team" className="bg-[#141414]">Team Member</option>
                                <option value="staff" className="bg-[#141414]">Event Staff</option>
                            </select>
                        </label>
                        {role === "staff" && (
                            <label className="block">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Event</span>
                                <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="team-share-event">
                                    <option value="" className="bg-[#141414]">Select an event…</option>
                                    {events.map((e) => (
                                        <option key={e.id} value={e.id} className="bg-[#141414]">{e.name}</option>
                                    ))}
                                </select>
                            </label>
                        )}
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Expires in</span>
                            <select value={expiresHours} onChange={(e) => setExpiresHours(Number(e.target.value))} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="team-share-expiry">
                                {EXPIRY_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value} className="bg-[#141414]">{o.label}</option>
                                ))}
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Channel (optional)</span>
                            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] py-2 outline-none font-mono uppercase text-xs" data-testid="team-share-channel">
                                {CHANNELS.map((c) => <option key={c.value} value={c.value} className="bg-[#141414]">{c.label}</option>)}
                            </select>
                        </label>
                    </div>
                    <DialogFooter>
                        <button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="team-share-submit">
                            <Link2 className="w-4 h-4 mr-2" /> Generate link
                        </button>
                    </DialogFooter>
                </>
            ) : (
                <>
                    <div className="space-y-3">
                        <div className="text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-2"><Ticket className="w-3 h-3" /> Share this link</div>
                        <div className="border border-[#2a2a2a] p-3 bg-[#141414] font-mono text-xs break-all" data-testid="team-share-url">{created.url}</div>
                        <div className="text-[10px] font-mono uppercase text-[#666]">
                            Role: {ROLE_LABEL[created.role] || created.role} · Expires: {new Date(created.expires_at).toLocaleString()}
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={copyCreated} className="pm-btn pm-btn-primary" data-testid="team-share-copy-created"><Copy className="w-4 h-4 mr-2" /> Copy link</button>
                        <button onClick={close} className="pm-btn" data-testid="team-share-done">Done</button>
                    </DialogFooter>
                </>
            )}
        </DialogContent>
    );
}
